import { Button } from 'tio-ui/components/buttons';
import { concat, hash } from '@ember/helper';
import { dropTask } from 'ember-concurrency';
import { eq, not, or } from 'tio-ui/utilities';
import { Input, Textarea } from 'tio-ui/components/forms';
import { on } from '@ember/modifier';
import { Popover } from 'tio-ui/components/popover';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import { divide } from 'tio-ui/math';
import inputmask from '../../../modifiers/inputmask.ts';
import safeFormatNumber from '../../../helpers/safe-format-number.ts';
import TioErrorMessages from '../../tio/error-messages.gts';
import type Owner from '@ember/owner';
import type TasApplicationModel from '../../../models/tas-application.ts';
import { PhX, PhCheck, PhPencil } from 'ember-phosphor-icons';

export interface TuitionAssistanceApplicationDetailsEditableApplicationTotalSignature {
  Args: {
    approvedTotal: number;
    courseCashTotal?: number;
    application: TasApplicationModel;
    onSave: (
      approvedTotal: number,
      reason: string,
      approvedCourseCashTotalField?: Record<string, unknown>
    ) => void;
    onCancel?: () => void;
  };
}

export default class TuitionAssistanceApplicationDetailsEditableApplicationTotalComponent extends Component<TuitionAssistanceApplicationDetailsEditableApplicationTotalSignature> {
  @tracked editableApprovedTotal = 0;
  @tracked editableCourseCashTotal = 0;
  @tracked isEditingApprovedTotal = false;
  @tracked reasonForEdit = '';
  @tracked submitError = null;
  @tracked didSubmit = false;

  @service sessionContext: unknown;

  constructor(
    owner: Owner,
    args: TuitionAssistanceApplicationDetailsEditableApplicationTotalSignature['Args']
  ) {
    super(owner, args);
    this.syncEditableApprovedTotal();
  }

  setApplicationTotal = (_maskedValue: unknown, event?: InputEvent) => {
    // @ts-expect-error: input mask needs types
    const unmaskedValue = event?.target.inputmask.unmaskedvalue();
    const approvedTotal = Math.round(Number(unmaskedValue) * 100);
    this.editableApprovedTotal = approvedTotal;
  };

  setCourseCashTotal = (_maskedValue: unknown, event?: InputEvent) => {
    // @ts-expect-error: input mask needs types
    const unmaskedValue = event?.target.inputmask.unmaskedvalue();
    const courseCashTotal = Math.round(Number(unmaskedValue) * 100);
    this.editableCourseCashTotal = courseCashTotal;
  };

  toggleIsEditing = () => {
    this.isEditingApprovedTotal = !this.isEditingApprovedTotal;
  };

  cancel = () => {
    this.submitError = null;
    this.isEditingApprovedTotal = false;
    this.didSubmit = false;
    this.syncEditableApprovedTotal();
    this.args.onCancel?.();
  };

  updateReasonForEdit = (reason: string) => {
    this.reasonForEdit = reason;
  };

  saveField = dropTask(async () => {
    const additionalFields: Record<string, unknown> = {};

    if (this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH') {
      additionalFields['CARD_FINAL_APPROVED_TOTAL'] = this.editableCourseCashTotal;
    }
    try {
      this.submitError = null;
      this.didSubmit = true;
      await this.args.onSave(this.editableApprovedTotal, this.reasonForEdit, additionalFields);
      this.isEditingApprovedTotal = false;
      this.syncEditableApprovedTotal();
      this.didSubmit = false;
    } catch (e) {
      console.error(e);
      this.submitError = e;
    }
  });

  get isSaving() {
    return this.saveField.isRunning;
  }

  get isValid() {
    return !!this.reasonForEdit && this.editableApprovedTotal >= 0;
  }

  syncEditableApprovedTotal() {
    this.editableApprovedTotal = this.args.approvedTotal;
    this.reasonForEdit = '';
    if (this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH') {
      this.editableCourseCashTotal = this.args.courseCashTotal ?? 0;
    }
  }

  <template>
    <div class="items-center flex justify-end text-right gap-4">
      <div>
        {{#if (eq @application.paymentMethod "TAS.AssistanceManner.COURSE_CASH")}}
          <div>
            <div class="text-sm text-tio-gray-400">
              Course Cash - Tuition:
            </div>
            <div>
              {{safeFormatNumber
                (divide (or @courseCashTotal 0) 100)
                style="currency"
                currency="USD"
              }}
            </div>
          </div>
          <div>
            <div class="text-sm text-tio-gray-400">
              All other expenses:
            </div>
            <div>
              {{safeFormatNumber (divide @approvedTotal 100) style="currency" currency="USD"}}
            </div>
          </div>
        {{else}}
          <div>
            {{safeFormatNumber (divide @approvedTotal 100) style="currency" currency="USD"}}
          </div>
        {{/if}}
      </div>
      <Popover
        @isOpen={{this.isEditingApprovedTotal}}
        @onOpenChange={{this.toggleIsEditing}}
        @placement="bottom-end"
        as |p|
      >
        <Button
          @appearance="outlined"
          @intent="primary"
          title={{t "common.edit"}}
          class="self-center"
          {{p.trigger}}
          {{p.anchor}}
          data-test-edit-approved-total
        >
          <PhPencil @size="20" />
        </Button>
        <p.Content @size="xl">
          <form class="flex flex-col gap-4 p-4">
            {{#if (eq @application.paymentMethod "TAS.AssistanceManner.COURSE_CASH")}}
              <Input
                @value={{String (divide (or this.editableCourseCashTotal 0) 100)}}
                @onChange={{this.setCourseCashTotal}}
                {{inputmask
                  alias="currency"
                  prefix="$"
                  unmaskAsNumber=true
                  digits="2"
                  autoUnmask=true
                }}
                {{!-- disabled={{@isSubmitting}} --}}
                @description="Course Cash - Tuition"
                @classes={{hash base="mb-2"}}
              />
            {{/if}}
            <Input
              data-test-application-total-input
              @value={{String (divide @approvedTotal 100)}}
              @onChange={{this.setApplicationTotal}}
              @description={{if
                (eq @application.paymentMethod "TAS.AssistanceManner.COURSE_CASH")
                "All other expenses"
                ""
              }}
              {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
            />
            <Textarea
              data-test-reason-for-edit
              @value={{this.reasonForEdit}}
              @onInput={{this.updateReasonForEdit}}
              @label={{concat (t "common.comment") "*"}}
              @classes={{hash base="text-xs"}}
            />
            <div class="flex gap-2 justify-end">
              <Button
                @appearance="outlined"
                @intent="warning"
                {{on "click" this.cancel}}
                disabled={{this.isSaving}}
                title="Cancel"
              >
                <PhX @size="20" />
              </Button>
              <Button
                @appearance="outlined"
                @intent="primary"
                {{on "click" this.saveField.perform}}
                @isRunning={{this.isSaving}}
                disabled={{not this.isValid}}
                title="Save"
              >
                <PhCheck @size="20" />
              </Button>
            </div>
            <TioErrorMessages
              @showErrors={{this.didSubmit}}
              @error={{this.submitError}}
              class="text-xs"
            />
          </form>
        </p.Content>
      </Popover>
    </div>
  </template>
}
